// SAB-427 / SAB-578: animated "Trash scoring — live" card.
//
// Rows derive from the single canonical example (trashScoringExample), shared with
// the illustrative side of the marketing story. The bogus "Missed field goal" row
// is gone (QBs do not attempt field goals) and the old SAB-447 mobile 3-row
// truncation is removed, so every breakpoint shows all four events and the visible
// subtotals always sum to the displayed total (+60).
//
// SAB-578 item 3 — the reveal must never REST at +0:
//   - The count-up plays ONCE, when the card scrolls into view (IntersectionObserver),
//     and then SETTLES on the real end-state (all rows scored, total +60). It does
//     NOT loop back to zero (the old looping reset is what a screenshot caught at +0).
//   - prefers-reduced-motion, or no IntersectionObserver support, renders the real
//     end-state immediately (never +0, no animation).
// The unrevealed rows show a "· · ·" live-resolving affordance; the running total
// only counts revealed rows, so the shown numbers always add up.
import { useEffect, useRef, useState } from 'react';
import { TRASH_EXAMPLE, trashSubtotal, TRASH_TOTAL } from './trashScoringExample';

interface Play {
  play: string;
  detail: string;
  pts: number;
}

const PLAYS: Play[] = TRASH_EXAMPLE.map((e) => ({
  play: e.label,
  detail: e.detail ?? '',
  pts: trashSubtotal(e),
}));

const TICK_MS = 1500;

interface ScoreboardState {
  addIdx: number;
  total: number;
  flashIdx: number;
}

const INITIAL: ScoreboardState = { addIdx: 0, total: 0, flashIdx: -1 };
const END_STATE: ScoreboardState = { addIdx: PLAYS.length, total: TRASH_TOTAL, flashIdx: -1 };

export function ScoreboardPanel() {
  const rootRef = useRef<HTMLDivElement>(null);
  const playedRef = useRef(false);
  const [s, setS] = useState<ScoreboardState>(INITIAL);

  useEffect(() => {
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const el = rootRef.current;
    // No motion / no observer support: show the real end-state, never +0.
    if (reduced || !el || typeof IntersectionObserver === 'undefined') {
      setS(END_STATE);
      return;
    }

    let intervalId = 0;
    let settleId = 0;

    const play = () => {
      let idx = 0;
      let running = 0;
      const reveal = () => {
        running += PLAYS[idx].pts;
        idx += 1;
        setS({ addIdx: idx, total: running, flashIdx: idx - 1 });
        if (idx >= PLAYS.length) {
          window.clearInterval(intervalId);
          intervalId = 0;
          // Settle on the real end-state (clears the last flash, holds +60).
          settleId = window.setTimeout(() => setS(END_STATE), 500);
        }
      };
      reveal(); // reveal the first row immediately so the low point is never +0
      intervalId = window.setInterval(reveal, TICK_MS);
    };

    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting && !playedRef.current) {
            playedRef.current = true;
            play();
            io.disconnect();
          }
        }
      },
      { threshold: 0.35 },
    );
    io.observe(el);

    return () => {
      io.disconnect();
      if (intervalId) window.clearInterval(intervalId);
      if (settleId) window.clearTimeout(settleId);
    };
  }, []);

  return (
    <div
      ref={rootRef}
      className="relative p-[22px] rounded-xl border border-border bg-card"
      style={{ boxShadow: 'var(--shadow-lg)' }}
    >
      {/* Header row */}
      <div className="flex items-center justify-between mb-1.5">
        <div className="flex items-center gap-[9px]">
          <span
            className="w-2 h-2 rounded-full bg-damage"
            style={{ animation: 'sf-marketing-pulse 1.6s infinite' }}
          />
          <span className="font-display text-[11px] uppercase tracking-[0.06em] font-bold text-foreground">
            Trash scoring: live
          </span>
        </div>
        <span className="font-display text-[10px] uppercase tracking-[0.06em] font-bold text-muted-foreground/70">Example</span>
      </div>

      <p className="text-sm text-muted-foreground mt-0 mb-3.5">
        Your QB1&rsquo;s worst Sunday, scored in real time.
      </p>

      {/* Rows */}
      <div className="flex flex-col gap-0.5 mb-4">
        {PLAYS.map((p, i) => {
          const scored = i < s.addIdx;
          const isFlash = i === s.flashIdx;
          return (
            <div
              key={p.play}
              className="flex items-center justify-between rounded-md px-3 py-2.5"
              style={{
                background: isFlash ? 'hsl(0 84% 60% / 0.16)' : 'transparent',
                transition: 'background 350ms ease',
              }}
            >
              <div className="min-w-0">
                <div className="font-bold text-[14.5px] text-foreground">{p.play}</div>
                {p.detail && (
                  <div className="text-xs text-muted-foreground">{p.detail}</div>
                )}
              </div>
              <div
                className={[
                  'font-display font-extrabold tabular-nums text-base',
                  scored ? 'text-damage' : 'text-muted-foreground/50',
                ].join(' ')}
              >
                {scored ? `+${p.pts}` : '· · ·'}
              </div>
            </div>
          );
        })}
      </div>

      {/* Total */}
      <div className="flex items-center justify-between border-t border-border pt-4">
        <span className="font-display font-bold text-sm uppercase tracking-[0.05em] text-muted-foreground">
          Trash total
        </span>
        <span className="font-varsity text-gradient-sabotage text-[46px] leading-none">
          +{s.total}
        </span>
      </div>

      {/* SAB-578 item 2: offsets qualifier (same as the real-game card). */}
      <p className="text-xs text-muted-foreground/80 mt-3">
        Trash points, before offsets. Completions and yards score against you.
      </p>

      {/* Keyframes local to this component to avoid name collisions */}
      <style>{`
        @keyframes sf-marketing-pulse {
          0%, 100% { box-shadow: 0 0 0 0 hsl(0 84% 60% / 0.5); }
          50%      { box-shadow: 0 0 0 6px hsl(0 84% 60% / 0); }
        }
      `}</style>
    </div>
  );
}

export default ScoreboardPanel;
